import { computed, inject, Injectable, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { OnboardingStepKey } from '../../features/onboarding/onboarding-utils';
import { OnboardingApiService } from '../../features/onboarding/services/onboarding-api.service';

@Injectable({
  providedIn: 'root'
})
export class OnboardingStateService {
  private readonly onboardingApiService = inject(OnboardingApiService);

  private readonly _isComplete = signal<boolean | null>(null);
  private readonly _requiredSteps = signal<OnboardingStepKey[]>([]);
  private readonly _isLoading = signal<boolean>(false);
  private _hasLoaded = false;

  readonly isComplete = computed(() => this._isComplete());
  readonly requiredSteps = computed(() => this._requiredSteps());
  readonly isLoading = computed(() => this._isLoading());
  readonly needsOnboarding = computed(() => this._isComplete() === false);

  /**
   * Load onboarding status from API if not already loaded.
   * Returns true if onboarding is complete, false otherwise.
   */
  async loadOnboardingStatusAsync(): Promise<boolean> {
    if (this._hasLoaded) {
      return this._isComplete() ?? false;
    }

    this._isLoading.set(true);

    try {
      const steps = await firstValueFrom(this.onboardingApiService.getUsersRequiredSteps());
      this._hasLoaded = true;
      this._requiredSteps.set(steps ?? []);

      const isComplete = !steps || steps.length === 0;
      this._isComplete.set(isComplete);

      return isComplete;
    } catch (error) {
      console.error('Error loading onboarding status:', error);
      return true;
    } finally {
      this._isLoading.set(false);
    }
  }

  /**
   * Force refresh the onboarding status from API.
   * Use after user completes steps to get updated status.
   */
  async refreshStatusAsync(): Promise<void> {
    this._hasLoaded = false;
    await this.loadOnboardingStatusAsync();
  }

  /**
   * Mark onboarding as complete.
   * Called when user finishes the final portal step.
   */
  markComplete(): void {
    this._isComplete.set(true);
    this._requiredSteps.set([]);
  }

  /**
   * Reset the onboarding state.
   * Call on logout to clear cached status.
   */
  reset(): void {
    this._hasLoaded = false;
    this._isComplete.set(null);
    this._requiredSteps.set([]);
    this._isLoading.set(false);
  }
}
